import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../state/store";
import { selectConsentElements } from "../../state/selectors";
import {
  insertConsentElement,
  alignConsentElementTexts,
  setConsentsLoading,
  setConsentElementsVisibility
} from "../../state/ducks";

import { alignItems, consentStatuses, strCode } from "../../consts";
import { consentsService, log, notificationService, t } from "../../services";
import { consentUtil } from "../../utils";
import { withSection } from "../../hoc";

import { NewConsentForm } from "../../containers";
import {
  AppManagementBreadcrumbs,
  FlexContainers,
  Heading5,
  PageContainer,
  WrapperContainer
} from "../../components";

const EditConsentElementPage = () => {
  const consentElements = useSelector((store) => store.consents.consentElements);
  const selectedConsent = useSelector((store) => store.consents.selectedConsent);
  const selectedConsentElement = useSelector((store) => store.consents.selectedConsentElement);
  const consentElementsVisibility = useSelector(
    (store) => store.consents.consentElementsVisibility
  );
  const isViewOnly = useSelector((store) => store.consents.isViewOnly);
  const isActive = selectedConsent?.status === consentStatuses.active;

  const viewOnlyLangs = useMemo(() => {
    const originalConsentElement = selectedConsent?.items?.find(
      (item) => item?.id === selectedConsentElement?.id
    );

    return originalConsentElement?.texts
      ? originalConsentElement.texts?.map((text) => {
          if (text?.content?.length) {
            return text.languageCode;
          }
        })
      : [];
  }, [selectedConsent, selectedConsentElement]);

  const onEditConsentElement = (consent, isVisible) => {
    const updatedAtTs = new Date().getTime();
    const editedConsentElement = Object.assign({}, { ...selectedConsentElement });

    if (!isVisible) {
      delete editedConsentElement?.groupName;
      delete editedConsentElement?.type;
      delete editedConsentElement?.code;
      delete editedConsentElement?.texts;
    }

    const newConsentElement = { ...editedConsentElement, ...consent, updatedAtTs };

    dispatch(insertConsentElement(newConsentElement));

    if (isVisible) {
      dispatch(alignConsentElementTexts(newConsentElement));
    }

    dispatch(setConsentElementsVisibility(isVisible));

    if (isViewOnly) {
      updateConsentElementsTexts();
    }
  };

  const updateConsentElementsTexts = useCallback(async () => {
    dispatch(setConsentsLoading(true));

    try {
      const consentElements = selectConsentElements()(getState());

      const formattedConsentElements = consentUtil.getConsentElementUpdatedTexts(consentElements);
      const updatedConsentElements = [];

      formattedConsentElements?.forEach((item) => {
        updatedConsentElements.push({
          consentsId: selectedConsent.id,
          ...item
        });
      });

      await consentsService.updateConsentElementsTexts(updatedConsentElements);

      notificationService.showSuccess({
        title: t(strCode.UPDATE_CONSENT_SUCCESS),
        message: t(strCode.CONSENT_UPDATE_SUCCESS)
      });
    } catch (err) {
      log.error("EditConsentElementPage: Failed to update consent", err);

      notificationService.showError({
        title: t(strCode.UPDATE_CONSENT_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
      throw err;
    } finally {
      dispatch(setConsentsLoading(false));
    }
  }, [selectedConsent]);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%"
        height="100%">
        <AppManagementBreadcrumbs />

        {selectedConsentElement ? (
          <Heading5>
            {t(strCode.VIEW_CONSENT_ELEMENT, { name: selectedConsentElement?.name })}
          </Heading5>
        ) : (
          <Heading5>{t(strCode.NEW_CONSENT_ELEMENT)}</Heading5>
        )}

        <NewConsentForm
          consentsData={selectedConsentElement}
          onSaveConsent={onEditConsentElement}
          isViewMode={isViewOnly}
          viewOnlyLangs={viewOnlyLangs}
          isActive={isActive}
          isToggleVisibilityDisabled={
            consentElementsVisibility !== null && consentElements?.length > 1
          }
        />
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(EditConsentElementPage);
